/* eslint-disable react/prop-types */
import React, { useState } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDAlert from "components/MDAlert";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PendingIcon from "@mui/icons-material/Pending";
import VisibilityIcon from "@mui/icons-material/Visibility";

function InvoicesList({ 
  invoices = [], 
  eventId, 
  onInvoiceCreated, 
  onInvoiceDeleted,
  isAdmin 
}) {
  const [newInvoiceId, setNewInvoiceId] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [syncingInvoiceId, setSyncingInvoiceId] = useState(null);
  const [syncingAll, setSyncingAll] = useState(false);

  const [sendingReminder, setSendingReminder] = useState(null);

  const apiUrl = process.env.REACT_APP_AUTH_API_URL;

  const handleCreateInvoice = async () => {
    if (!newInvoiceId.trim()) {
      setError("Inserisci un ID fattura valido");
      return;
    }

    setIsCreating(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch(`${apiUrl}/event/update_invoice.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ eventId, invoice: newInvoiceId }),
      });

      const result = await response.json();

      if (!response.ok || result.status === "error") {
        throw new Error(result.message || "Failed to create invoice");
      }

      if (result.invoiceError) {
        setError(result.invoiceError);
      } else {
        setSuccess("Fattura creata con successo!");
        setNewInvoiceId("");
        if (onInvoiceCreated) {
          onInvoiceCreated(result);
        }
      }
    } catch (err) {
      console.error("Error creating invoice:", err);
      setError(err.message || "Errore durante la creazione della fattura");
    } finally {
      setIsCreating(false);
    }
  };

  const handleSyncInvoice = async (invoiceDbId) => {
    setSyncingInvoiceId(invoiceDbId);
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch(`${apiUrl}/event/sync_invoice.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ invoiceDbId }),
      });

      const result = await response.json();

      if (!response.ok || result.status === "error") {
        throw new Error(result.message || "Errore durante la sincronizzazione");
      }

      setSuccess("Fattura sincronizzata con successo!");
      if (onInvoiceCreated) {
        onInvoiceCreated(result);
      }
    } catch (err) {
      console.error("Error syncing invoice:", err);
      setError(err.message || "Errore durante la sincronizzazione");
    } finally {
      setSyncingInvoiceId(null);
    }
  };

  const handleSendPaymentReminder = async (invoice) => {
    setSendingReminder(invoice.db_id);
    setError(null);
    setSuccess(null);
    try {
      const amount = invoice.details?.data?.amount_due ?? invoice.details?.data?.amount_gross ?? '';
      const dueDate = invoice.details?.data?.payment_method?.payment_terms?.[0]?.days
        ? ''
        : '';
      const response = await fetch(`${apiUrl}/event/request_payment.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          eventId,
          invoiceDbId: invoice.db_id,
          invoiceAmount: amount ? Number(amount).toFixed(2) : '',
          invoiceDueDate: dueDate,
          invoiceType: invoice.invoice_type,
          invoiceUrl: invoice.details?.data?.url ?? '',
          invoiceNumber: invoice.invoice_id,
        }),
      });
      const result = await response.json();
      if (!response.ok || result.status === 'error') throw new Error(result.message);
      setSuccess('Email promemoria inviata!');
    } catch (err) {
      setError(err.message || 'Errore durante invio promemoria');
    } finally {
      setSendingReminder(null);
    }
  };

  const handleSyncAllInvoices = async () => {
    setSyncingAll(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch(`${apiUrl}/event/sync_all_invoices.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ eventId }),
      });

      const result = await response.json();

      if (!response.ok || result.status === "error") {
        throw new Error(result.message || "Errore durante la sincronizzazione");
      }

      setSuccess(`Sincronizzate ${result.synced} fatture con successo!`);
      if (result.errors && result.errors.length > 0) {
        setError(`Errori: ${result.errors.map(e => e.invoice_id).join(', ')}`);
      }
      if (onInvoiceCreated) {
        onInvoiceCreated(result);
      }
    } catch (err) {
      console.error("Error syncing all invoices:", err);
      setError(err.message || "Errore durante la sincronizzazione");
    } finally {
      setSyncingAll(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("it-IT", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <MDBox>
      {/* Create new invoice section - Only for admins */}
      {isAdmin && (
        <Card sx={{ mb: 3 }}>
          <MDBox p={3}>
            <MDTypography variant="h6" mb={2}>
              Crea Nuova Fattura/Preventivo
            </MDTypography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={8}>
                <MDInput
                  type="text"
                  label="ID Fattura (FattureInCloud)"
                  value={newInvoiceId}
                  onChange={(e) => setNewInvoiceId(e.target.value)}
                  fullWidth
                  disabled={isCreating}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <MDButton
                  variant="gradient"
                  color="success"
                  fullWidth
                  onClick={handleCreateInvoice}
                  disabled={isCreating}
                >
                  <Icon>add</Icon>&nbsp;
                  {isCreating ? "Creazione..." : "Crea Fattura"}
                </MDButton>
              </Grid>
            </Grid>

            {error && (
              <MDAlert color="error" dismissible sx={{ mt: 2 }}>
                {error}
              </MDAlert>
            )}

            {success && (
              <MDAlert color="success" dismissible sx={{ mt: 2 }}>
                {success}
              </MDAlert>
            )}
          </MDBox>
        </Card>
      )}

      {/* Invoices list */}
      <MDBox>
        <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <MDTypography variant="h6">
            Fatture/Preventivi ({invoices.length})
          </MDTypography>
          {isAdmin && invoices.length > 0 && (
            <MDButton
              variant="outlined"
              color="info"
              size="small"
              onClick={handleSyncAllInvoices}
              disabled={syncingAll}
            >
              <Icon>sync</Icon>&nbsp;
              {syncingAll ? "Sincronizzazione..." : "Sync Tutte"}
            </MDButton>
          )}
        </MDBox>

        {invoices.length === 0 ? (
          <MDBox textAlign="center" py={4}>
            <Icon sx={{ fontSize: 48, color: "text.secondary", mb: 2 }}>receipt_long</Icon>
            <MDTypography variant="body2" color="text">
              Nessuna fattura associata a questo evento
            </MDTypography>
          </MDBox>
        ) : (
          <Grid container spacing={2}>
            {invoices.map((invoice) => (
              <Grid item xs={12} key={invoice.db_id}>
                <Card variant="outlined">
                  <MDBox p={2}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} md={2}>
                        <MDBox textAlign="center">
                          {invoice.invoice_type === 'preventivo' ? (
                            invoice.confirmed === 1 ? (
                              <Tooltip title="Confermato">
                                <CheckCircleIcon sx={{ fontSize: 40, color: "success.main" }} />
                              </Tooltip>
                            ) : invoice.confirmed === 2 ? (
                              <Tooltip title="Rifiutato">
                                <Icon sx={{ fontSize: 40, color: "error.main" }}>cancel</Icon>
                              </Tooltip>
                            ) : (
                              <Tooltip title="In attesa di conferma">
                                <PendingIcon sx={{ fontSize: 40, color: "warning.main" }} />
                              </Tooltip>
                            )
                          ) : (
                            <Tooltip title={invoice.invoice_type === 'fattura' ? 'Fattura' : 'Ordine'}>
                              <Icon sx={{ fontSize: 40, color: "info.main" }}>
                                {invoice.invoice_type === 'fattura' ? 'receipt' : 'shopping_cart'}
                              </Icon>
                            </Tooltip>
                          )}
                        </MDBox>
                      </Grid>

                      <Grid item xs={12} md={7}>
                        <MDTypography variant="h6" fontWeight="medium">
                          {invoice.invoice_type === 'fattura' ? 'Fattura' : invoice.invoice_type === 'ordine' ? 'Ordine' : 'Preventivo'} #{invoice.invoice_id}
                        </MDTypography>
                        <MDTypography variant="caption" color="text">
                          Creata: {formatDate(invoice.created_at)}
                        </MDTypography>
                        {invoice.invoice_type === 'preventivo' && invoice.confirmed_at && (
                          <>
                            <br />
                            <MDTypography variant="caption" color={invoice.confirmed === 1 ? "success" : "error"}>
                              {invoice.confirmed === 1 ? "Confermato" : "Rifiutato"}: {formatDate(invoice.confirmed_at)}
                            </MDTypography>
                          </>
                        )}
                        {invoice.details?.data?.amount_gross && (
                          <>
                            <br />
                            <MDTypography variant="body2" fontWeight="bold" color="info">
                              Importo: €{Number(invoice.details.data.amount_gross).toFixed(2)}
                            </MDTypography>
                          </>
                        )}
                        {(invoice.invoice_type === 'fattura' || invoice.invoice_type === 'ordine') && invoice.details?.data && (() => {
                          const gross = parseFloat(invoice.details.data.amount_gross ?? 0);
                          const due = parseFloat(invoice.details.data.amount_due ?? gross);
                          let label, color;
                          if (due <= 0) {
                            label = 'Pagata'; color = 'success';
                          } else if (due < gross) {
                            label = `Pagata parzialmente (residuo €${due.toFixed(2)})`; color = 'warning';
                          } else {
                            label = 'Non pagata'; color = 'error';
                          }
                          return (
                            <>
                              <br />
                              <MDTypography variant="caption" fontWeight="bold" color={color}>
                                {label}
                              </MDTypography>
                            </>
                          );
                        })()}
                        {invoice.error && (
                          <>
                            <br />
                            <MDTypography variant="caption" color="error">
                              Errore: {invoice.error}
                            </MDTypography>
                          </>
                        )}
                      </Grid>

                      <Grid item xs={12} md={3}>
                        <MDBox display="flex" gap={1} justifyContent="flex-end">
                          {(invoice.invoice_type === 'fattura' || invoice.invoice_type === 'ordine') && (
                            <Tooltip title="Invia promemoria pagamento">
                              <IconButton
                                color="warning"
                                onClick={() => handleSendPaymentReminder(invoice)}
                                disabled={sendingReminder === invoice.db_id}
                              >
                                <Icon>
                                  {sendingReminder === invoice.db_id ? 'hourglass_empty' : 'notifications_active'}
                                </Icon>
                              </IconButton>
                            </Tooltip>
                          )}
                          {isAdmin && (
                            <Tooltip title="Sincronizza da FattureInCloud">
                              <IconButton
                                color="secondary"
                                onClick={() => handleSyncInvoice(invoice.db_id)}
                                disabled={syncingInvoiceId === invoice.db_id}
                              >
                                <Icon>
                                  {syncingInvoiceId === invoice.db_id ? "hourglass_empty" : "sync"}
                                </Icon>
                              </IconButton>
                            </Tooltip>
                          )}
                          
                          {invoice.details?.data?.url && (
                            <Tooltip title="Apri PDF">
                              <IconButton
                                color="info"
                                onClick={() => window.open(invoice.details.data.url, "_blank")}
                              >
                                <Icon>picture_as_pdf</Icon>
                              </IconButton>
                            </Tooltip>
                          )}
                          
                          {invoice.invoice_type === 'preventivo' && invoice.confirmation_link && invoice.confirmed === 0 && (
                            <Tooltip title="Link di conferma">
                              <IconButton
                                color="primary"
                                onClick={() => window.open(invoice.confirmation_link, "_blank")}
                              >
                                <VisibilityIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </MDBox>
                      </Grid>
                    </Grid>
                  </MDBox>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </MDBox>
    </MDBox>
  );
}

export default InvoicesList;
